import escapeRegExp from 'lodash/escapeRegExp';

export type DiffType = 'unchanged' | 'changed' | 'added' | 'removed';

/**
 * Diff type of every path in either record, unchanged ones included: without
 * them a subtree holding nothing but additions could equally be a new entry or
 * an old one that gained a field.
 */
export type DiffMap = Record<string, DiffType>;

export type Leaf = string | number | boolean;

export const joinPath = (base: string | undefined, source: string): string =>
  base ? `${base}.${source}` : source;

export type IdMatchedPaths = readonly string[];

/**
 * Collections matched by element `id` rather than by position. The default is
 * positional, because for most collections the order is part of the value —
 * reordering contacts is a change. Listed here are the ones that are a set
 * rather than a sequence, where inserting or removing an element should not make
 * everything after it look changed. A path may use `[*]` for any element of an
 * enclosing collection, as in `documents[*].files`.
 */
const ID_MATCHED_PATHS: Record<string, IdMatchedPaths> = {
  individuals: ['documents', 'documents[*].files'],
  businesses: ['documents', 'documents[*].files'],
};

/** Shared so that a resource with no entry keeps the same identity every call. */
const NONE: IdMatchedPaths = [];

export const idMatchedPaths = (resource?: string): IdMatchedPaths =>
  ID_MATCHED_PATHS[resource ?? ''] ?? NONE;

const matchesPattern = (path: string, pattern: string): boolean => {
  if (!pattern.includes('[*]')) return path === pattern;

  const expression = pattern.split('[*]').map(escapeRegExp).join('\\[[^\\]]*\\]');

  return new RegExp(`^${expression}$`).test(path);
};

export const isIdMatched = (path: string, idMatched: IdMatchedPaths): boolean =>
  idMatched.some((pattern) => matchesPattern(path, pattern));

export const itemKey = (item: any, index: number, byId: boolean): string => {
  const id = item?.id;
  if (byId && id !== undefined && id !== null && id !== '') return String(id);
  return String(index);
};

export const joinItemPath = (base: string, key: string | number): string => `${base}[${key}]`;

/**
 * Empty objects and arrays produce no entries, and neither do `null` and
 * `undefined`: a property with no value is the same as one that is not there.
 */
export const flattenRecord = (
  value: any,
  prefix = '',
  idMatched: IdMatchedPaths = []
): Record<string, Leaf> => {
  const result: Record<string, Leaf> = {};
  if (value == null || typeof value !== 'object') return result;

  if (Array.isArray(value)) {
    const byId = isIdMatched(prefix, idMatched);
    value.forEach((item, index) => {
      if (item == null) return;
      const key = joinItemPath(prefix, itemKey(item, index, byId));
      if (typeof item === 'object') {
        Object.assign(result, flattenRecord(item, key, idMatched));
      } else {
        result[key] = item;
      }
    });
    return result;
  }

  Object.entries(value).forEach(([property, propertyValue]) => {
    const key = joinPath(prefix, property);
    if (propertyValue == null) return;
    if (typeof propertyValue === 'object') {
      Object.assign(result, flattenRecord(propertyValue, key, idMatched));
    } else {
      result[key] = propertyValue as Leaf;
    }
  });
  return result;
};

export const buildDiffMap = (
  original: any,
  draft: any,
  idMatched: IdMatchedPaths = []
): DiffMap => {
  const flatOriginal = flattenRecord(original, '', idMatched);
  const flatDraft = flattenRecord(draft, '', idMatched);
  const map: DiffMap = {};

  new Set([...Object.keys(flatOriginal), ...Object.keys(flatDraft)]).forEach((path) => {
    const inOriginal = Object.prototype.hasOwnProperty.call(flatOriginal, path);
    const inDraft = Object.prototype.hasOwnProperty.call(flatDraft, path);

    if (!inOriginal) map[path] = 'added';
    else if (!inDraft) map[path] = 'removed';
    else if (!Object.is(flatOriginal[path], flatDraft[path])) map[path] = 'changed';
    else map[path] = 'unchanged';
  });

  return map;
};

export const isUnderPath = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}.`) || path.startsWith(`${prefix}[`);

export const pathDiff = (map: DiffMap, path: string): DiffType | undefined =>
  map[path] ?? groupDiff(map, path);

/**
 * Diff of a whole subtree, for elements rendered as one block — a table row, a
 * card, a feature panel. 'added'/'removed' only when the entry as a whole is new
 * or gone; one that merely gained or lost a part is the same entry, altered.
 */
export const groupDiff = (map: DiffMap, prefix: string): DiffType | undefined => {
  const types = Object.keys(map)
    .filter((path) => isUnderPath(path, prefix))
    .map((path) => map[path]);

  if (!types.length) return undefined;
  if (types.every((type) => type === 'unchanged')) return undefined;
  if (types.every((type) => type === 'added')) return 'added';
  if (types.every((type) => type === 'removed')) return 'removed';
  return 'changed';
};
